import { Injectable } from '@angular/core';
import { ISiteMap, ISiteMapContentItem } from '@fi-shared/models/site-map';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { IWidgetSettingsModel } from '@fi-shared/widget/widget-settings.model';

export interface ISitemapWidgetTagExpandedChange {
    key: string;
    expanded: boolean;
}

export interface ISitemapWidgetContentIndentParams {
    item: ISiteMapContentItem;
    settings: IWidgetSettingsModel;
    contentIndentPx: number;
    contentLevel: number;
    isThumbnailLevelWithContentItems: boolean;
}

export interface ISitemapWidgetLevelOneVisibilityParams {
    item: ISiteMapContentItem;
    hideLevelOneItemsOnCollapse: boolean;
}

export interface ISitemapWidgetNestedContentIndentParams {
    settings: IWidgetSettingsModel;
    contentIndentPx: number;
    contentLevel: number;
    expandedItemIndentPx: number;
    measuredNamePrefixWidthPx: number;
}

@Injectable({ providedIn: 'root' })
export class SitemapWidgetHelperService {
    private readonly baseIndentPx = 16;
    private readonly nestedThumbnailTagIndentOffsetPx = 32;
    private readonly firstLevelThumbnailTagContentOffsetPx = 32;
    private readonly namePrefixNameGapPx = 5;
    private readonly thumbnailNameGapPx = 8;

    getItemKey(item: ISiteMapContentItem): string {
        return `${item.ParentId}-${item.ObjectType}-${item.ObjectId}-${item.NamePrefix ?? ''}`;
    }

    getExpandToggleButtonText(siteMap: ISiteMap | null, isAllExpanded: boolean): string {
        const isTagItemPresent = this.hasTags(siteMap?.TagData?.ChildItems ?? []);
        if (isTagItemPresent) {
            return isAllExpanded ? 'common.collapse_all' : 'common.expand_all';
        }

        return isAllExpanded ? 'common.collapse' : 'common.expand';
    }

    getExpandableTagKeys(siteMap: ISiteMap | null): string[] {
        if (!siteMap?.TagData?.ChildItems) {
            return [];
        }

        const result: string[] = [];
        this.addExpandableTagKeys(siteMap.TagData.ChildItems, result);
        return result;
    }

    isInitiallyAllExpanded(siteMap: ISiteMap | null): boolean {
        return !!siteMap && this.getExpandableTagKeys(siteMap).length === 0;
    }

    isItemNotEmpty(item: ISiteMapContentItem): boolean {
        return (
            item.ObjectType !== ObjectTypes.Tag ||
            item.ChildItems.some((childItem: ISiteMapContentItem) => this.isItemNotEmpty(childItem))
        );
    }

    isItemWithViewAccess(item: ISiteMapContentItem): boolean {
        return item.ObjectType !== ObjectTypes.Tag
            ? item.HasViewAccess
            : item.ChildItems.some((child: ISiteMapContentItem) => this.isItemWithViewAccess(child));
    }

    isLevelOneContentItemHiddenOnCollapse(params: ISitemapWidgetLevelOneVisibilityParams): boolean {
        const { item, hideLevelOneItemsOnCollapse } = params;
        return (
            hideLevelOneItemsOnCollapse &&
            !item.ChildItems.length &&
            item.Level === 1 &&
            item.ObjectType !== ObjectTypes.Tag
        );
    }

    getContentItemIndentPx(params: ISitemapWidgetContentIndentParams): number {
        const { item, settings, contentIndentPx, contentLevel, isThumbnailLevelWithContentItems } = params;
        if (!settings?.displayThumbnails || item.ObjectType !== ObjectTypes.Tag) {
            return contentIndentPx;
        }

        if (contentLevel === 1) {
            return isThumbnailLevelWithContentItems
                ? contentIndentPx + this.firstLevelThumbnailTagContentOffsetPx
                : contentIndentPx;
        }

        return contentIndentPx + this.nestedThumbnailTagIndentOffsetPx;
    }

    getNestedContentIndentPx(params: ISitemapWidgetNestedContentIndentParams): number {
        const { settings, contentIndentPx, contentLevel, expandedItemIndentPx, measuredNamePrefixWidthPx } = params;
        if (settings?.displayThumbnails) {
            return expandedItemIndentPx;
        }

        if (contentLevel + 1 === 1) {
            return this.baseIndentPx;
        }

        if (settings?.displayNumbering) {
            return contentIndentPx + measuredNamePrefixWidthPx + this.getNamePrefixNameGapPx(settings);
        }

        return contentIndentPx + this.baseIndentPx;
    }

    hasVisibleContentItems(
        items: ISiteMapContentItem[],
        isItemVisible: (item: ISiteMapContentItem) => boolean,
    ): boolean {
        return items.some((item: ISiteMapContentItem) => isItemVisible(item) && item.ObjectType !== ObjectTypes.Tag);
    }

    areNumberMapsEqual(previous: ReadonlyMap<string, number>, next: ReadonlyMap<string, number>): boolean {
        if (previous.size !== next.size) {
            return false;
        }

        for (const [key, width] of next) {
            if (previous.get(key) !== width) {
                return false;
            }
        }

        return true;
    }

    private getNamePrefixNameGapPx(settings: IWidgetSettingsModel): number {
        return settings?.displayThumbnails ? this.thumbnailNameGapPx : this.namePrefixNameGapPx;
    }

    private hasTags(items: ISiteMapContentItem[]): boolean {
        return items.some(
            (item: ISiteMapContentItem) =>
                item.ObjectType === ObjectTypes.Tag || this.hasTags(item.ChildItems ?? []),
        );
    }

    private addExpandableTagKeys(items: ISiteMapContentItem[], result: string[]): void {
        items.forEach((item: ISiteMapContentItem) => {
            if (item.ObjectType !== ObjectTypes.Tag) {
                return;
            }

            if (item.ChildItems.length) {
                result.push(this.getItemKey(item));
                this.addExpandableTagKeys(item.ChildItems, result);
            }
        });
    }
}
